import React, { useState, useMemo } from 'react';
import { Button, Chip, Divider, IconButton, CircularProgress } from '@mui/material';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { getSiteAuditIcons } from 'src/common/config/FunctionHelper';
import TitleTooltip from 'src/components/UI/TitleTooltip';
import {
  ContentOpportunitiesDataV2,
  ContentOpportunityItemV2
} from '../../types/content-strategy-v2.type';
import OpportunityDetailsDrawer from './OpportunityDetailsDrawer';

interface ContentOpportunitiesProps {
  data?: ContentOpportunitiesDataV2;
  page: number;
  onPageChange: (newPage: number) => void;
  // Todo: Content Plan - Add to plan callback props
  onAddToPlan?: (opportunityId: string) => void;
  isAddingToPlan?: boolean;
  onIgnore: (opportunityId: string) => void;
  isLoading?: boolean;
}

const formatPriority = (priority?: string) => {
  if (!priority) return '';
  return `${priority.charAt(0).toUpperCase()}${priority.slice(1).toLowerCase()} Priority`;
};

const formatIntent = (intent?: string) => {
  if (!intent) return '';
  return `${intent.charAt(0).toUpperCase()}${intent.slice(1).toLowerCase()}`;
};

const formatTraffic = (traffic?: number) => {
  if (traffic === undefined) return '0/month';
  return `${traffic.toLocaleString()}/month`;
};

const getPriorityChipClasses = (priority?: string) => {
  const p = priority?.toLowerCase();
  if (p === 'high') return '!bg-[#FEE4E2] !text-[#D92D20] !font-medium';
  if (p === 'medium') return '!bg-[#FEF0C7] !text-[#B54708] !font-medium';
  if (p === 'low') return '!bg-[#D1FADF] !text-[#027A48] !font-medium';
  return '!bg-[#F2F4F7] !text-[#344054] !font-medium';
};

export const ContentOpportunities: React.FC<ContentOpportunitiesProps> = ({
  data,
  page,
  onPageChange,
  onAddToPlan,
  onIgnore,
  isAddingToPlan = false,
  isLoading = false
}) => {
  const [selectedOpportunity, setSelectedOpportunity] =
    useState<ContentOpportunityItemV2 | null>(null);
  const [exitingIds, setExitingIds] = useState<Set<string>>(new Set());

  const items = data?.items || [];

  const activeOpportunity = useMemo(() => {
    if (!selectedOpportunity) return null;
    return items.find((item) => item.id === selectedOpportunity.id) || selectedOpportunity;
  }, [items, selectedOpportunity]);

  const total = data?.total || 0;
  const limit = data?.limit || 3;
  const totalPages = Math.ceil(total / limit) || 1;
  const topRecId = data?.top_recommendation_id;

  const handleIgnoreRow = (id: string) => {
    setExitingIds((prev) => new Set([...prev, id]));
    setTimeout(() => {
      onIgnore(id);
    }, 250);
  };

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 bg-white/70 z-50 flex items-center justify-center backdrop-blur-[1px] rounded-xl">
          <CircularProgress size={40} sx={{ color: '#120242' }} />
        </div>
      )}
      {/* Header */}
      <div className="flex justify-between items-center px-6 py-2">
        <TitleTooltip>
          <TitleTooltip.Text
            title="Content Opportunities"
            className="!text-[var(--color-text-secondary)] !font-medium !text-base"
          >
            <TitleTooltip.Tooltip
              tooltip={
                <p className="text-primary text-sm font-medium">
                  AI-identified content opportunities ranked by priority,
                  search volume, and traffic potential.
                </p>
              }
            />
          </TitleTooltip.Text>
        </TitleTooltip>

        {total > 0 && (
          <div className="flex items-center gap-2">
            <p className="text-primary text-base font-medium">
              {page}/{totalPages}
            </p>

            <div className="flex items-center">
              <IconButton
                size="small"
                onClick={() => onPageChange(page - 1)}
                disabled={page <= 1}
              >
                <KeyboardArrowLeftIcon />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => onPageChange(page + 1)}
                disabled={page >= totalPages}
              >
                <NavigateNextIcon />
              </IconButton>
            </div>
          </div>
        )}
      </div>

      <Divider />

      {items.length > 0 ? (
        items.map((item, index) => {
          const isTopRec = item.id === topRecId;
          const isPlanned = item.status === 'planned';
          const isExiting = exitingIds.has(item.id);
          const actionType = item.actions?.generate_content?.type || 'generatePagePlan';

          if (isExiting) return null;

          return (
            <div key={item.id}>
              <div
                id={`opportunity-${item.id}`}
                className="px-6 py-4 flex justify-between items-center"
              >
                {/* LEFT */}
                <div className="flex-1">
                  {/* TOP */}
                  <div className="flex items-center gap-4 mb-3 flex-wrap">
                    <p className="font-semibold text-base text-[#1D2939]">
                      {item.suggested_title || item.topic}
                    </p>

                    {/* Keyword */}
                    <div className="flex items-center justify-between bg-[#F2F4F7] rounded px-3 h-[38px]">
                      <p className="text-sm text-[#344054] font-medium mr-2">
                        {item.target_keyword || item.topic}
                      </p>

                      <IconButton
                        size="small"
                        onClick={() =>
                          navigator.clipboard.writeText(
                            item.target_keyword || item.topic
                          )
                        }
                      >
                        <img
                          src="/static/images/icons/copyIcon.svg"
                          style={{
                            width: '18px',
                            height: '18px',
                            objectFit: 'contain'
                          }}
                          alt="copyIcon"
                        />
                      </IconButton>
                    </div>

                    {isTopRec && (
                      <div className="flex items-center gap-2">
                        <img
                          src={getSiteAuditIcons('ai-visibility.svg')}
                          style={{
                            width: '18px',
                            height: '18px',
                            objectFit: 'contain'
                          }}
                          alt="ai-visibility"
                        />
                        <p className="text-sm text-[#475467]">
                          AI Top Recommendation
                        </p>
                      </div>
                    )}

                    {/* Todo: Content Plan - In Plan badge indicator */}
                    {/*
                    {isPlanned && (
                      <Chip
                        label={
                          <span className="text-xs font-medium">✓ In Plan</span>
                        }
                        size="small"
                        className="!bg-[#D1FADF] !text-[#027A48] !font-medium"
                      />
                    )}
                    */}
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <Chip
                      label={
                        <span className="text-xs font-medium">
                          {formatPriority(item.priority)}
                        </span>
                      }
                      size="small"
                      className={getPriorityChipClasses(item.priority)}
                    />

                    <Chip
                      label={
                        <span className="text-xs font-medium">
                          {item.content_type?.replace('_', ' ') || 'Page'}
                        </span>
                      }
                      size="small"
                      className="!bg-[#F2F4F7] capitalize"
                    />

                    <Chip
                      label={
                        <span className="text-xs font-medium">{`Volume: ${
                          item.search_volume?.toLocaleString() || 0
                        }`}</span>
                      }
                      size="small"
                      className="!bg-[#F2F4F7]"
                    />

                    <Chip
                      label={
                        <span className="text-xs font-medium">{`Difficulty: ${
                          item.difficulty || 0
                        }`}</span>
                      }
                      size="small"
                      className="!bg-[#F2F4F7]"
                    />

                    <Chip
                      label={
                        <span className="text-xs font-medium">{`Intent: ${formatIntent(
                          item.search_intent
                        )}`}</span>
                      }
                      size="small"
                      className="!bg-[#F2F4F7]"
                    />

                    <Chip
                      label={
                        <span className="text-xs font-medium">{`Traffic Potential: ${formatTraffic(
                          item.estimated_traffic_potential
                        )}`}</span>
                      }
                      size="small"
                      className="!bg-[#F2F4F7]"
                    />

                    {item.opportunity_category && (
                      <Chip
                        label={
                          <span className="text-xs font-medium capitalize">
                            {item.opportunity_category.replace('_', ' ')}
                          </span>
                        }
                        size="small"
                        className="!bg-[#E6F9F7] !text-[#0D6F66] !font-medium"
                      />
                    )}
                  </div>
                </div>

                {/* RIGHT */}
                <div className="flex flex-col lg:flex-row gap-4 ml-8">
                  {/* Todo: Content Plan - Add to Plan action button */}
                  {/*
                  {!isPlanned && onAddToPlan && (
                    <Button
                      variant="outlined"
                      className="px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
                      onClick={() => onAddToPlan(item.id)}
                      disabled={isAddingToPlan}
                    >
                      Add to Plan
                    </Button>
                  )}
                  */}

                  <Button
                    variant="outlined"
                    className="px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
                    onClick={() => setSelectedOpportunity(item)}
                  >
                    Details
                  </Button>

                  {/* {actionType === 'redirectToBlogAgent' ? (
                    <Button
                      variant="contained"
                      className="px-4 h-10 !bg-[#120242] !text-white !normal-case !font-semibold"
                      onClick={() => setSelectedOpportunity(item)}
                    >
                      Open in Blog Agent
                    </Button>
                  ) : item.page_plan ? (
                    <Button
                      variant="outlined"
                      className="px-4 h-10 !border-[#120242] !text-[#120242] !normal-case !font-semibold"
                      onClick={() => setSelectedOpportunity(item)}
                    >
                      View Page Plan
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      className="px-4 h-10 !bg-[#120242] !text-white !normal-case !font-semibold"
                      onClick={() => setSelectedOpportunity(item)}
                    >
                      Generate Page Plan
                    </Button>
                  )} */}

                  <Button
                    variant="outlined"
                    className="px-4 h-10 !border-[#D92D20] !text-[#D92D20] !normal-case !font-semibold"
                    onClick={() => handleIgnoreRow(item.id)}
                  >
                    Ignore
                  </Button>
                </div>
              </div>

              {index !== items.length - 1 && <Divider />}
            </div>
          );
        })
      ) : (
        <div className="px-6 py-8 text-center text-[#667085] text-sm font-normal">
          No data found
        </div>
      )}

      {/* Details Drawer */}
      <OpportunityDetailsDrawer
        open={Boolean(selectedOpportunity)}
        onClose={() => setSelectedOpportunity(null)}
        opportunity={activeOpportunity}
        onAddToPlan={onAddToPlan}
        onIgnore={handleIgnoreRow}
        isAddingToPlan={isAddingToPlan}
      />
    </div>
  );
};

export default ContentOpportunities;
